import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EMPTY_SERVICE_BASELINE, type ServiceSavedBaseline } from "@/lib/service-price-save";
import { ServiceTemplatePicker } from "../ServiceTemplatePicker";
import { ServicePriceField } from "../ServicePriceField";
import type { ServiceFormState } from "../types";
import type { ServiceTemplateKey } from "../onboarding-logic";
import { ServiceMembersField } from "@/components/session-members/ServiceMembersField";

interface Props {
  service: ServiceFormState;
  onServiceChange: (s: ServiceFormState) => void;
  selectedTemplate: ServiceTemplateKey | null;
  onSelectTemplate: (key: ServiceTemplateKey) => void;
  /** Baseline (cijena bez PDV-a + sirovi bruto) ucitane cijene usluge koja se uredjuje, ako postoji. */
  savedBaseline?: ServiceSavedBaseline;
  /** Adresa ordinacije; traži se samo kad se usluga održava i uživo. */
  officeAddress: string;
  onOfficeAddressChange: (v: string) => void;
}

export function ServicesStep({
  service,
  onServiceChange,
  selectedTemplate,
  onSelectTemplate,
  savedBaseline,
  officeAddress,
  onOfficeAddressChange,
}: Props) {
  const set = (patch: Partial<ServiceFormState>) => onServiceChange({ ...service, ...patch });
  return (
    <div className="space-y-6">
      <ServiceTemplatePicker selected={selectedTemplate} onSelect={onSelectTemplate} />
      <hr className="border-[oklch(90%_0.02_84)]" />
      <div className="space-y-2">
        <Label htmlFor="svc-name">Naziv usluge</Label>
        <Input id="svc-name" className="bg-card" value={service.name} onChange={(e) => set({ name: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="svc-desc">Kratak opis</Label>
        <Textarea id="svc-desc" className="bg-card" value={service.description} onChange={(e) => set({ description: e.target.value })} placeholder="Za koga je i šta klijent može očekivati…" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Trajanje</Label>
          <Select value={String(service.durationMinutes)} onValueChange={(v) => set({ durationMinutes: Number(v) })}>
            <SelectTrigger className="w-full bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[30, 45, 50, 60, 90].map((m) => (
                <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Kako se održava</Label>
          <Select value={service.modality} onValueChange={(v) => set({ modality: v as ServiceFormState["modality"] })}>
            <SelectTrigger className="w-full bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="in_person">Uživo</SelectItem>
              <SelectItem value="both">Oboje</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {service.modality !== "online" && (
        <div className="space-y-2">
          <Label htmlFor="office">Adresa ordinacije</Label>
          <Input
            id="office"
            className="bg-card"
            value={officeAddress}
            onChange={(e) => onOfficeAddressChange(e.target.value)}
            placeholder="npr. Titova 12, Sarajevo"
          />
          <p className="text-xs text-muted-foreground">Klijent je vidi kad rezerviše seansu uživo.</p>
        </div>
      )}

      <ServiceMembersField idPrefix="onboarding-service" value={service.maxMembers} onChange={(v) => set({ maxMembers: v })} />

      <ServicePriceField
        price={service.price}
        onPriceChange={(v) => set({ price: v })}
        baseline={savedBaseline ?? EMPTY_SERVICE_BASELINE}
      />
    </div>
  );
}
